// Airtable-style filter bar (Dean, 15 Oct 2026): inline condition rows, AND/OR groups, removable chips. No modal on desktop;
// a bottom sheet on phones. Pure UI over filterEngine — the page decides what rows the tree is applied to.
import { useState } from "react";
import { Filter, Plus, X, Trash2, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BottomSheet } from "@/components/phone/PhoneBits";
import { useIsPhone } from "@/hooks/useIsPhone";
import { OPS, DATE_WORDS, newCond, newGroup, isGroup, needsValue, countConds, condLabel, emptyTree } from "./filterEngine";

export function FilterBar({ fields, tree, onChange, testid = "filter-bar", className = "", children }) {
  const [open, setOpen] = useState(false);
  const isPhone = useIsPhone();
  const n = countConds(tree);
  const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));
  const removeCond = (id) => onChange(dropId(tree, id));
  const panel = <FilterPanel fields={fields} tree={tree} onChange={onChange} onDone={() => setOpen(false)} testid={testid} />;
  return (
    <div className={`space-y-2 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        <button type="button" onClick={() => setOpen((o) => !o)}
          className={`inline-flex items-center gap-1.5 h-7 px-2 rounded-md border transition-colors ${n ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
          aria-expanded={open} data-testid={`${testid}-toggle`}>
          <Filter className="w-3.5 h-3.5" />
          <span className="font-semibold">Filter</span>
          {n > 0 && <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/20" data-testid={`${testid}-count`}>{n}</span>}
        </button>
        <Chips tree={tree} byKey={byKey} onRemove={removeCond} testid={testid} />
        {n > 0 && (
          <button type="button" onClick={() => onChange(emptyTree())} className="h-7 px-2 text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`${testid}-clear`}>
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        {children}
      </div>
      {open && (isPhone
        ? <BottomSheet open onClose={() => setOpen(false)} title="Filter" testid={`${testid}-sheet`}>{panel}</BottomSheet>
        : <div className="border rounded-md bg-white shadow-sm p-2.5" data-testid={`${testid}-panel`}>{panel}</div>)}
    </div>
  );
}

function Chips({ tree, byKey, onRemove, testid }) {
  const out = [];
  const walk = (t, depth) => {
    (t.items || []).forEach((it, i) => {
      if (isGroup(it)) { walk(it, depth + 1); return; }
      const joiner = out.length ? (depth ? t.join : tree.join) : null;
      out.push(
        <span key={it.id} className="inline-flex items-center gap-1">
          {joiner && <span className="text-[10px] uppercase text-slate-400 font-semibold">{joiner}</span>}
          <span className={`inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-md border text-[11px] ${depth ? "bg-sky-50 border-sky-200 text-sky-900" : "bg-slate-50 border-slate-200 text-slate-800"}`} data-testid={`${testid}-chip-${i}`}>
            {condLabel(it, byKey)}
            <button type="button" onClick={() => onRemove(it.id)} className="w-4 h-4 inline-flex items-center justify-center rounded hover:bg-black/10" aria-label="Remove condition" data-testid={`${testid}-chip-remove-${it.id}`}><X className="w-3 h-3" /></button>
          </span>
        </span>,
      );
    });
  };
  walk(tree, 0);
  return out;
}

export function FilterPanel({ fields, tree, onChange, onDone, testid }) {
  const setItems = (items) => onChange({ ...tree, items });
  const update = (id, patch) => setItems(tree.items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      {tree.items.length === 0 && <div className="text-xs text-slate-500 px-1">No conditions yet — every row shows.</div>}
      {tree.items.map((it, i) => (
        <div key={it.id} className="flex items-start gap-2">
          <JoinCell index={i} join={tree.join} onJoin={(j) => onChange({ ...tree, join: j })} />
          {isGroup(it)
            ? <GroupEditor fields={fields} group={it} onChange={(g) => update(it.id, g)} onRemove={() => setItems(tree.items.filter((x) => x.id !== it.id))} testid={`${testid}-group-${i}`} />
            : <CondRow fields={fields} cond={it} onChange={(c) => update(it.id, c)} onRemove={() => setItems(tree.items.filter((x) => x.id !== it.id))} testid={`${testid}-cond-${i}`} />}
        </div>
      ))}
      <div className="flex items-center gap-1 flex-wrap pt-1">
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setItems([...tree.items, newCond(fields[0])])} data-testid={`${testid}-add-cond`}><Plus className="w-3 h-3 mr-1" /> Add condition</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setItems([...tree.items, { ...newGroup(), items: [newCond(fields[0])] }])} data-testid={`${testid}-add-group`}><ListFilter className="w-3 h-3 mr-1" /> Add condition group</Button>
        {onDone && <Button size="sm" className="h-7 text-xs ml-auto" onClick={onDone} data-testid={`${testid}-done`}>Done</Button>}
      </div>
    </div>
  );
}

function JoinCell({ index, join, onJoin }) {
  if (index === 0) return <span className="w-[62px] shrink-0 h-8 inline-flex items-center text-xs text-slate-500 pl-1">Where</span>;
  if (index > 1) return <span className="w-[62px] shrink-0 h-8 inline-flex items-center text-xs text-slate-500 pl-1 uppercase">{join}</span>;
  return (
    <Select value={join} onValueChange={onJoin}>
      <SelectTrigger className="h-8 w-[62px] shrink-0 text-xs uppercase" data-testid="filter-join"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="and">and</SelectItem><SelectItem value="or">or</SelectItem></SelectContent>
    </Select>
  );
}

function GroupEditor({ fields, group, onChange, onRemove, testid }) {
  const setItems = (items) => onChange({ ...group, items });
  const update = (id, patch) => setItems(group.items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  return (
    <div className="flex-1 min-w-0 border border-sky-200 bg-sky-50/40 rounded-md p-2 space-y-1.5" data-testid={testid}>
      {group.items.map((it, i) => (
        <div key={it.id} className="flex items-start gap-2">
          <JoinCell index={i} join={group.join} onJoin={(j) => onChange({ ...group, join: j })} />
          <CondRow fields={fields} cond={it} onChange={(c) => update(it.id, c)} onRemove={() => setItems(group.items.filter((x) => x.id !== it.id))} testid={`${testid}-cond-${i}`} />
        </div>
      ))}
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setItems([...group.items, newCond(fields[0])])} data-testid={`${testid}-add`}><Plus className="w-3 h-3 mr-1" /> Add to group</Button>
        <Button size="sm" variant="ghost" className="h-6 text-[11px] text-rose-600 ml-auto" onClick={onRemove} data-testid={`${testid}-remove`}><Trash2 className="w-3 h-3 mr-1" /> Remove group</Button>
      </div>
    </div>
  );
}

function CondRow({ fields, cond, onChange, onRemove, testid }) {
  const f = fields.find((x) => x.key === cond.field) || fields[0];
  const ops = OPS[f.type] || OPS.text;
  const pickField = (key) => { const nf = fields.find((x) => x.key === key); onChange({ field: key, op: (OPS[nf.type] || OPS.text)[0][0], value: nf.type === "date" ? "today" : "", value2: undefined }); };
  return (
    <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      <Select value={f.key} onValueChange={pickField}>
        <SelectTrigger className="h-8 w-[170px] text-xs" data-testid={`${testid}-field`}><SelectValue /></SelectTrigger>
        <SelectContent>{fields.map((x) => <SelectItem key={x.key} value={x.key} className="text-xs">{x.label}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={cond.op} onValueChange={(op) => onChange({ op, value: needsValue(op) ? cond.value : "" })}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`${testid}-op`}><SelectValue /></SelectTrigger>
        <SelectContent>{ops.map(([k, l]) => <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>)}</SelectContent>
      </Select>
      {needsValue(cond.op) && <ValueInput field={f} cond={cond} onChange={onChange} testid={testid} />}
      <button type="button" onClick={onRemove} className="h-8 w-7 inline-flex items-center justify-center rounded text-slate-400 hover:text-rose-600" aria-label="Remove" data-testid={`${testid}-remove`}><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ValueInput({ field, cond, onChange, testid }) {
  const t = field.type;
  if (t === "select") {
    const multi = cond.op === "any_of" || cond.op === "none_of";
    if (multi) {
      const picked = String(cond.value || "").split("|").filter(Boolean);
      const toggle = (v) => onChange({ value: (picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]).join("|") });
      return (
        <div className="flex items-center gap-1 flex-wrap max-w-[520px]" data-testid={`${testid}-multi`}>
          {(field.options || []).map((o) => (
            <button key={o.value} type="button" onClick={() => toggle(String(o.value))}
              className={`h-7 px-2 rounded-md border text-[11px] ${picked.includes(String(o.value)) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-400"}`}
              data-testid={`${testid}-opt-${o.value}`}>{o.label}</button>
          ))}
        </div>
      );
    }
    return (
      <Select value={String(cond.value ?? "")} onValueChange={(v) => onChange({ value: v })}>
        <SelectTrigger className="h-8 w-[200px] text-xs" data-testid={`${testid}-value`}><SelectValue placeholder="Pick…" /></SelectTrigger>
        <SelectContent>{(field.options || []).map((o) => <SelectItem key={o.value} value={String(o.value)} className="text-xs">{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (t === "number") {
    return (
      <>
        <Input type="number" value={cond.value ?? ""} onChange={(e) => onChange({ value: e.target.value })} className="h-8 w-[110px] text-xs" data-testid={`${testid}-value`} />
        {cond.op === "between" && <><span className="text-xs text-slate-500">and</span><Input type="number" value={cond.value2 ?? ""} onChange={(e) => onChange({ value2: e.target.value })} className="h-8 w-[110px] text-xs" data-testid={`${testid}-value2`} /></>}
      </>
    );
  }
  if (t === "date") {
    if (cond.op === "next_days" || cond.op === "past_days") {
      return <><Input type="number" min={0} value={cond.value ?? ""} onChange={(e) => onChange({ value: e.target.value })} className="h-8 w-[80px] text-xs" data-testid={`${testid}-value`} /><span className="text-xs text-slate-500">days</span></>;
    }
    return (
      <>
        <DatePick value={cond.value} onChange={(v) => onChange({ value: v })} testid={`${testid}-value`} />
        {cond.op === "between" && <><span className="text-xs text-slate-500">and</span><DatePick value={cond.value2} onChange={(v) => onChange({ value2: v })} testid={`${testid}-value2`} /></>}
      </>
    );
  }
  return <Input value={cond.value ?? ""} onChange={(e) => onChange({ value: e.target.value })} placeholder="Type a value…" className="h-8 w-[200px] text-xs" data-testid={`${testid}-value`} />;
}

function DatePick({ value, onChange, testid }) {
  const isWord = DATE_WORDS.some(([k]) => k === value);
  const mode = isWord || !value ? value || "today" : "exact";
  return (
    <span className="inline-flex items-center gap-1">
      <Select value={mode} onValueChange={(v) => onChange(v === "exact" ? new Date().toISOString().slice(0, 10) : v)}>
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`${testid}-mode`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {DATE_WORDS.map(([k, l]) => <SelectItem key={k} value={k} className="text-xs">{l}</SelectItem>)}
          <SelectItem value="exact" className="text-xs">exact date…</SelectItem>
        </SelectContent>
      </Select>
      {mode === "exact" && <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-8 w-[150px] text-xs" data-testid={testid} />}
    </span>
  );
}

function dropId(tree, id) {
  return { ...tree, items: tree.items.filter((it) => it.id !== id).map((it) => (isGroup(it) ? dropId(it, id) : it)).filter((it) => !isGroup(it) || it.items.length) };
}
